// Rule evaluation + greedy swap proposer.
//
// Semantics: a rule between A and B only applies to activities where BOTH
// are assigned to some group. Unassigned = rule does not count that activity.

export const RULE_TYPES = [
  { value: "never_with",   label: "should never sit with",            needsCount: false },
  { value: "always_with",  label: "should always sit with",           needsCount: false },
  { value: "at_least",     label: "should sit with at least N times", needsCount: true  },
  { value: "no_more_than", label: "should sit with at most N times",  needsCount: true  },
];

function applicableActivities(rule, activities) {
  if (!rule.activity_types || rule.activity_types.length === 0) return activities;
  const set = new Set(rule.activity_types);
  return activities.filter(a => set.has(a.type));
}

function cumulativeStats(aId, bId, activities) {
  let together = 0, bothPresent = 0;
  for (const act of activities) {
    const gA = act.groups.find(g => g.people.includes(aId));
    const gB = act.groups.find(g => g.people.includes(bId));
    if (gA && gB) {
      bothPresent++;
      if (gA.id === gB.id) together++;
    }
  }
  return { together, bothPresent };
}

export function evalRules(rules, activities) {
  return rules.map(r => {
    const scoped = applicableActivities(r, activities);
    const { together, bothPresent } = cumulativeStats(r.person_a, r.person_b, scoped);
    let broken = false, reason = "";
    switch (r.type) {
      case "never_with":
        broken = together > 0;
        reason = `sat together ${together}× (should be 0)`;
        break;
      case "always_with":
        broken = bothPresent > 0 && together < bothPresent;
        reason = `together in ${together}/${bothPresent} eligible activities`;
        break;
      case "at_least":
        broken = together < (r.count ?? 0);
        reason = `together ${together}× (needs ≥ ${r.count})`;
        break;
      case "no_more_than":
        broken = together > (r.count ?? 0);
        reason = `together ${together}× (max ${r.count})`;
        break;
      default:
        break;
    }
    return { rule: r, together, bothPresent, broken, reason };
  });
}

function countBroken(rules, activities) {
  return evalRules(rules, activities).filter(e => e.broken).length;
}

// How far a rule is from being satisfied. 0 means satisfied; higher is worse.
// Gives us a signal for partial progress: an `at_least 3` rule currently at 1
// has score 2 — each incremental pairing reduces that by 1, so swaps that
// bring A and B together one more time are preferred even though the rule
// is still technically broken.
function ruleScore(rule, together, bothPresent) {
  switch (rule.type) {
    case "never_with":   return together;
    case "always_with":  return Math.max(0, bothPresent - together);
    case "at_least":     return Math.max(0, (rule.count ?? 0) - together);
    case "no_more_than": return Math.max(0, together - (rule.count ?? 0));
    default: return 0;
  }
}

function totalScore(rules, activities) {
  let s = 0;
  for (const r of rules) {
    const scoped = applicableActivities(r, activities);
    const { together, bothPresent } = cumulativeStats(r.person_a, r.person_b, scoped);
    s += ruleScore(r, together, bothPresent);
  }
  return s;
}

// Return a copy of activities with `personId` appended to the group in
// `activityId`/`groupId`. No-op if they're already there.
export function applyPlacement(activities, personId, activityId, groupId) {
  return activities.map(act => {
    if (act.id !== activityId) return act;
    return {
      ...act,
      groups: act.groups.map(gr =>
        gr.id === groupId && !gr.people.includes(personId)
          ? { ...gr, people: [...gr.people, personId] }
          : gr
      ),
    };
  });
}

// Choose which group in an activity to place a person into so as to
// minimise new rule violations. Uses the violation-score metric so that
// e.g. placing A next to B on an at_least rule is preferred even if the
// rule is still short of N. Ties broken by smaller current group size,
// then randomly. Returns the chosen group id.
export function pickGroupForPlacement(rules, activities, activityId, personId, candidateGroupIds) {
  if (candidateGroupIds.length === 1) return candidateGroupIds[0];
  const act = activities.find(a => a.id === activityId);
  let bestScore = Infinity, bestSize = Infinity, bestIds = [];
  for (const gid of candidateGroupIds) {
    const gr = act.groups.find(g => g.id === gid);
    const size = gr ? gr.people.length : 0;
    const sim = applyPlacement(activities, personId, activityId, gid);
    const score = totalScore(rules, sim);
    if (score < bestScore || (score === bestScore && size < bestSize)) {
      bestScore = score; bestSize = size; bestIds = [gid];
    } else if (score === bestScore && size === bestSize) {
      bestIds.push(gid);
    }
  }
  return bestIds[Math.floor(Math.random() * bestIds.length)];
}

// A swap moves personA (in groupA) and personB (in groupB) between groups
// within the same activity.
function applySwap(activities, swap) {
  return activities.map(act => {
    if (act.id !== swap.activityId) return act;
    return {
      ...act,
      groups: act.groups.map(gr => {
        if (gr.id === swap.aGroupId) {
          return { ...gr, people: gr.people.map(p => p === swap.aPersonId ? swap.bPersonId : p) };
        }
        if (gr.id === swap.bGroupId) {
          return { ...gr, people: gr.people.map(p => p === swap.bPersonId ? swap.aPersonId : p) };
        }
        return gr;
      }),
    };
  });
}

function candidatesFor(rule, activities) {
  const out = [];
  const A = rule.person_a, B = rule.person_b;
  const wantsTogether = rule.type === "always_with" || rule.type === "at_least";
  const wantsApart    = rule.type === "never_with"  || rule.type === "no_more_than";
  const scoped = applicableActivities(rule, activities);

  for (const act of scoped) {
    const gA = act.groups.find(g => g.people.includes(A));
    const gB = act.groups.find(g => g.people.includes(B));
    if (!gA || !gB) continue; // only swap in activities where both present

    if (wantsApart && gA.id === gB.id) {
      // Move A or B to a different group via swap with someone there.
      for (const other of act.groups) {
        if (other.id === gA.id) continue;
        for (const p of other.people) {
          if (p === A || p === B) continue;
          out.push({ activityId: act.id, aPersonId: A, aGroupId: gA.id, bPersonId: p, bGroupId: other.id });
          out.push({ activityId: act.id, aPersonId: B, aGroupId: gA.id, bPersonId: p, bGroupId: other.id });
        }
      }
    } else if (wantsTogether && gA.id !== gB.id) {
      // Bring A into B's group (swap A with someone in gB), or vice versa.
      for (const p of gB.people) {
        if (p === A || p === B) continue;
        out.push({ activityId: act.id, aPersonId: A, aGroupId: gA.id, bPersonId: p, bGroupId: gB.id });
      }
      for (const p of gA.people) {
        if (p === A || p === B) continue;
        out.push({ activityId: act.id, aPersonId: p, aGroupId: gA.id, bPersonId: B, bGroupId: gB.id });
      }
    }
  }
  return out;
}

// Greedy proposer: walk broken rules, propose one swap at a time that
// reduces the total violation score, applying each to a working copy so
// later proposals see the effect. Uses violation score rather than
// broken-rule count so partial progress counts — e.g. an `at_least 3`
// rule can be chipped away at one activity at a time.
export function proposeSwaps(rules, activities) {
  let working = activities;
  const proposals = [];
  let guard = 0;
  while (guard++ < 100) {
    const beforeScore = totalScore(rules, working);
    if (beforeScore === 0) break;
    const beforeBroken = countBroken(rules, working);

    const evals = evalRules(rules, working);
    const broken = evals.filter(e => e.broken);
    if (broken.length === 0) break;

    let picked = null;
    for (const ev of broken) {
      const cs = candidatesFor(ev.rule, working);
      for (const swap of cs) {
        const sim = applySwap(working, swap);
        const afterScore = totalScore(rules, sim);
        if (afterScore < beforeScore) {
          const afterBroken = countBroken(rules, sim);
          picked = {
            swap,
            ruleId: ev.rule.id,
            beforeScore, afterScore,
            beforeBroken, afterBroken,
          };
          break;
        }
      }
      if (picked) break;
    }
    if (!picked) break; // no improving swap found — greedy stops
    proposals.push({ id: `p${proposals.length}`, ...picked });
    working = applySwap(working, picked.swap);
  }
  return proposals;
}
